import { Dialog as DialogPrimitive } from "@base-ui/react/dialog";
import { Command as CommandPrimitive } from "cmdk";
import { Loader2, Search, XCircle } from "lucide-react";
import { useTranslate } from "ra-core";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
} from "react";

import { Command, CommandList } from "@/components/ui/command";
import { useTapThroughGuard } from "@/lib/tapThroughGuard";

import {
  RecentSearches,
  SearchBody,
  SearchHint,
  SearchTile,
} from "./SearchResults";
import { QUICK_LINKS, getDescriptor } from "./searchLinks";
import type { SearchController } from "./useSearchController";

/** Drag distance (px) past which releasing the header dismisses. */
const DISMISS_DISTANCE = 80;
/** Release speed (px/ms) of a downward flick that dismisses at any distance. */
const DISMISS_VELOCITY = 0.5;
/** Movement (px) before a press on the header becomes a drag. */
const DRAG_SLOP = 8;

/**
 * Keeps the overlay sized to the part of the screen above the keyboard
 * (iOS shrinks the visual viewport, not the layout one, when it opens), so
 * the end of the list can always be scrolled into view.
 */
const useVisualViewportBox = (node: HTMLElement | null) => {
  useEffect(() => {
    const viewport = window.visualViewport;
    if (!node || !viewport) return;
    const update = () => {
      node.style.setProperty("--search-vv-h", `${viewport.height}px`);
      node.style.setProperty("--search-vv-top", `${viewport.offsetTop}px`);
    };
    update();
    viewport.addEventListener("resize", update);
    viewport.addEventListener("scroll", update);
    return () => {
      viewport.removeEventListener("resize", update);
      viewport.removeEventListener("scroll", update);
    };
  }, [node]);
};

/** Same curve as --search-spring in styles/search.css. */
const OPEN_SPRING =
  "linear(0, 0.1356, 0.3947, 0.6431, 0.8284, 0.9443, 1.0046, 1.0281, 1.031, 1.0249, 1.0165, 1.0092, 1.0041, 1.001, 1)";
const OPEN_DURATION = 320;

/**
 * FLIP: the input capsule starts on the search trigger and springs to its
 * place. The offset and scale back onto the trigger also stay set as
 * --search-from-* for the close transition (styles/search.css).
 */
const useMorphFromOrigin = (
  node: HTMLElement | null,
  slotRef: RefObject<HTMLElement | null>,
  capsuleRef: RefObject<HTMLElement | null>,
  originRef?: RefObject<HTMLElement | null>,
) => {
  useLayoutEffect(() => {
    const origin = originRef?.current;
    const slot = slotRef.current;
    if (!node || !origin || !slot) return;
    const from = origin.getBoundingClientRect();
    const to = slot.getBoundingClientRect();
    if (from.width === 0 || to.width === 0) return;
    const x = from.left - to.left;
    const y = from.top - to.top;
    const scaleX = from.width / to.width;
    const scaleY = from.height / to.height;
    node.style.setProperty("--search-from-x", `${x}px`);
    node.style.setProperty("--search-from-y", `${y}px`);
    node.style.setProperty("--search-from-sx", `${scaleX}`);
    node.style.setProperty("--search-from-sy", `${scaleY}`);
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    capsuleRef.current?.animate(
      [
        { transform: `translate(${x}px, ${y}px) scale(${scaleX}, ${scaleY})` },
        { transform: "none" },
      ],
      { duration: OPEN_DURATION, easing: OPEN_SPRING },
    );
  }, [node, slotRef, capsuleRef, originRef]);
};

type DragState = {
  startY: number;
  offset: number;
  isActive: boolean;
  lastY: number;
  lastTime: number;
  velocity: number;
};

/**
 * Swipe the header down to dismiss: the overlay follows the finger (with a
 * rubber band upwards) while the backdrop clears; a long enough drag or a
 * fast flick closes it, anything else springs back.
 */
const useSwipeToDismiss = ({
  popupRef,
  backdropRef,
  inputRef,
  onDismiss,
}: {
  popupRef: RefObject<HTMLElement | null>;
  backdropRef: RefObject<HTMLElement | null>;
  inputRef: RefObject<HTMLInputElement | null>;
  onDismiss: () => void;
}) => {
  const drag = useRef<DragState | null>(null);

  const apply = (offset: number) => {
    const popup = popupRef.current;
    const backdrop = backdropRef.current;
    if (popup) popup.style.transform = `translate3d(0, ${offset}px, 0)`;
    if (backdrop) {
      backdrop.style.opacity = `${1 - Math.min(Math.max(offset, 0) / 480, 0.6)}`;
    }
  };

  const onPointerDown = (event: ReactPointerEvent<HTMLElement>) => {
    if (event.button !== 0) return;
    if ((event.target as Element).closest("button")) return;
    drag.current = {
      startY: event.clientY,
      offset: 0,
      isActive: false,
      lastY: event.clientY,
      lastTime: event.timeStamp,
      velocity: 0,
    };
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLElement>) => {
    const state = drag.current;
    if (!state) return;
    const distance = event.clientY - state.startY;
    if (!state.isActive) {
      if (Math.abs(distance) < DRAG_SLOP) return;
      if (distance < 0) {
        drag.current = null;
        return;
      }
      state.isActive = true;
      event.currentTarget.setPointerCapture(event.pointerId);
      inputRef.current?.blur();
      popupRef.current?.setAttribute("data-dragging", "");
      for (const element of [popupRef.current, backdropRef.current]) {
        if (element) element.style.transition = "none";
      }
    }
    const elapsed = Math.max(event.timeStamp - state.lastTime, 1);
    state.velocity = (event.clientY - state.lastY) / elapsed;
    state.lastY = event.clientY;
    state.lastTime = event.timeStamp;
    // Upwards it resists, like a rubber band.
    state.offset = distance >= 0 ? distance : -Math.sqrt(-distance) * 2;
    apply(state.offset);
  };

  const release = (canDismiss: boolean) => {
    const state = drag.current;
    drag.current = null;
    if (!state?.isActive) return;
    const popup = popupRef.current;
    const backdrop = backdropRef.current;
    popup?.removeAttribute("data-dragging");
    const shouldDismiss =
      canDismiss &&
      (state.offset > DISMISS_DISTANCE || state.velocity > DISMISS_VELOCITY);
    if (shouldDismiss) {
      // Carries on down from where the finger let go, fading out.
      popup?.setAttribute("data-swiped", "");
      if (popup) {
        popup.style.transition =
          "transform 200ms cubic-bezier(0.2, 0.8, 0.2, 1), opacity 200ms ease-in";
        popup.style.transform = `translate3d(0, ${state.offset + 160}px, 0)`;
        popup.style.opacity = "0";
      }
      if (backdrop) {
        backdrop.style.transition = "opacity 200ms ease-in";
        backdrop.style.opacity = "0";
      }
      onDismiss();
      return;
    }
    for (const element of [popup, backdrop]) {
      if (!element) continue;
      element.style.transition = "";
      element.style.transform = "";
      element.style.opacity = "";
    }
  };

  return {
    onPointerDown,
    onPointerMove,
    onPointerUp: () => release(true),
    onPointerCancel: () => release(false),
  };
};

const QuickLinkChips = ({ search }: { search: SearchController }) => {
  const translate = useTranslate();
  return (
    <section
      className="search-m-quick"
      aria-label={translate("crm.search.quick_links")}
    >
      {QUICK_LINKS.map(({ name, url }, index) => {
        const descriptor = getDescriptor(name);
        return (
          <button
            key={name}
            type="button"
            className="search-m-quick__chip"
            style={{ "--search-i": index } as CSSProperties}
            onClick={() => search.openUrl(url)}
          >
            <SearchTile resource={name} icon={descriptor.icon} />
            <span className="search-m-quick__label">
              {translate(descriptor.labelKey)}
            </span>
          </button>
        );
      })}
    </section>
  );
};

/**
 * Phone search: a full-height liquid-glass overlay whose input capsule grows
 * out of the top-bar search pill, with an iOS "Cancel" button, results
 * grouped in inset cards and a keyboard-aware list. See styles/search.css.
 */
export const MobileSearchOverlay = ({
  open,
  search,
  originRef,
}: {
  open: boolean;
  search: SearchController;
  originRef?: RefObject<HTMLElement | null>;
}) => {
  const translate = useTranslate();
  const inputRef = useRef<HTMLInputElement>(null);
  const slotRef = useRef<HTMLDivElement>(null);
  const capsuleRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement | null>(null);
  const [popup, setPopup] = useState<HTMLDivElement | null>(null);
  const setPopupNode = useCallback((node: HTMLDivElement | null) => {
    popupRef.current = node;
    setPopup(node);
  }, []);

  const dismiss = () => {
    search.rememberQuery();
    search.close();
  };

  useTapThroughGuard(open);
  useVisualViewportBox(popup);
  useMorphFromOrigin(popup, slotRef, capsuleRef, originRef);
  const dragHandlers = useSwipeToDismiss({
    popupRef,
    backdropRef,
    inputRef,
    onDismiss: dismiss,
  });

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) dismiss();
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Backdrop
          ref={backdropRef}
          className="search-m-backdrop"
        />
        <DialogPrimitive.Popup
          ref={setPopupNode}
          initialFocus={inputRef}
          finalFocus={originRef}
          className="search-m"
        >
          <DialogPrimitive.Title className="sr-only">
            {translate("crm.search.title")}
          </DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">
            {translate("crm.search.placeholder")}
          </DialogPrimitive.Description>
          <Command
            shouldFilter={false}
            disablePointerSelection
            label={translate("crm.search.title")}
            className="search-m__command"
          >
            <div className="search-m__header" {...dragHandlers}>
              <div ref={slotRef} className="search-m__slot">
                <div
                  ref={capsuleRef}
                  className="search-m__capsule liquid-glass"
                >
                  <Search
                    aria-hidden
                    className="size-[18px] shrink-0"
                    strokeWidth={2.25}
                  />
                  <CommandPrimitive.Input
                    ref={inputRef}
                    value={search.query}
                    onValueChange={search.changeQuery}
                    placeholder={translate("crm.search.placeholder")}
                    enterKeyHint="search"
                    className="search-m__input"
                    onKeyDown={(event) => {
                      // The keyboard's "Search" key: put the keyboard away to
                      // browse the results, instead of opening the first one.
                      if (event.key === "Enter") {
                        event.preventDefault();
                        search.rememberQuery();
                        inputRef.current?.blur();
                      }
                    }}
                  />
                  {search.isFetching ? (
                    <Loader2
                      role="status"
                      aria-label={translate("crm.search.searching")}
                      className="search-spinner size-4 shrink-0"
                    />
                  ) : null}
                  {search.query ? (
                    <button
                      type="button"
                      className="search-m__clear"
                      aria-label={translate("crm.search.clear_query")}
                      onClick={() => {
                        search.changeQuery("");
                        inputRef.current?.focus();
                      }}
                    >
                      <XCircle className="size-[18px]" strokeWidth={2} />
                    </button>
                  ) : null}
                </div>
              </div>
              <button
                type="button"
                className="search-m__cancel"
                onClick={dismiss}
              >
                {translate("crm.search.cancel")}
              </button>
            </div>
            <CommandList
              className="search-m__list"
              // The keyboard covers the bottom of the list: scrolling it means
              // the user is done typing, so put the keyboard away.
              onTouchStart={() => inputRef.current?.blur()}
            >
              <div className="search-m__body">
                <SearchBody
                  search={search}
                  emptyState={
                    <>
                      <QuickLinkChips search={search} />
                      <RecentSearches search={search} />
                      <SearchHint />
                    </>
                  }
                />
              </div>
            </CommandList>
          </Command>
        </DialogPrimitive.Popup>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};
